import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Cross } from "lucide-react";
import { FaYoutube } from "react-icons/fa";
import { TIMELINE } from "@/data/content";

const CARD_BG = ["bg-white text-obsidian", "bg-yellow text-obsidian", "bg-coral text-white"];

export const Timeline = () => {
  const scroller = useRef(null);

  const scrollBy = (dir) => {
    if (scroller.current) {
      scroller.current.scrollBy({ left: dir * 360, behavior: "smooth" });
    }
  };

  return (
    <section id="timeline" data-testid="timeline" className="py-20 md:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <span className="font-outfit text-xs uppercase tracking-widest text-white">The Journey</span>
            <h2 className="font-anton uppercase text-5xl md:text-7xl leading-[0.9] text-obsidian mt-3">
              From <span className="text-yellow">10 years old</span> to now
            </h2>
            <p className="font-outfit font-medium text-obsidian/80 mt-4 max-w-xl">
              Starting a YouTube channel at 10 in 2013 changed my life forever. Here&apos;s what happened next, drag or use the arrows to scroll. 👉
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="timeline-prev"
              onClick={() => scrollBy(-1)}
              className="h-12 w-12 rounded-full bg-obsidian text-white flex items-center justify-center hover:bg-coral transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              data-testid="timeline-next"
              onClick={() => scrollBy(1)}
              className="h-12 w-12 rounded-full bg-obsidian text-white flex items-center justify-center hover:bg-coral transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal scroller */}
      <div
        ref={scroller}
        data-testid="timeline-scroller"
        className="flex items-start gap-5 overflow-x-auto pb-6 px-5 md:px-10 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TIMELINE.map((item, i) => (
          <motion.div
            key={item.year}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            data-testid={`timeline-card-${i}`}
            className={`relative shrink-0 w-[280px] md:w-[320px] snap-start rounded-3xl p-6 flex flex-col shadow-xl border-2 ${
              item.highlight
                ? "bg-blue text-obsidian border-obsidian"
                : `${CARD_BG[i % CARD_BG.length]} border-transparent`
            }`}
          >
            <span className="font-anton uppercase text-3xl md:text-4xl leading-none">{item.year}</span>
            <div className="mt-2 h-1 w-12 bg-current opacity-30 rounded-full" />
            <p className="font-outfit font-semibold text-sm md:text-base mt-4 leading-snug">
              {item.text}
            </p>

            {item.cross && (
              <div className="mt-3 rounded-2xl border-2 border-obsidian/15 flex items-center justify-center py-10">
                <Cross size={72} strokeWidth={1.5} className="opacity-80" />
              </div>
            )}
            {(item.medias || (item.media ? [item.media.src] : [])).map((src, mi) => (
              <div key={mi} className="mt-3 rounded-2xl overflow-hidden border-2 border-obsidian/15 relative">
                <img
                  src={src}
                  alt={item.year}
                  loading="lazy"
                  className="w-full h-auto block"
                  data-testid={`timeline-media-${i}-${mi}`}
                />
                {item.caption && mi === 0 && (
                  <div
                    data-testid={`timeline-caption-${i}`}
                    className="absolute top-4 right-2 inline-flex items-center gap-1 bg-coral text-white font-outfit font-bold text-[9px] leading-tight px-2 py-1 rounded-full shadow-lg -rotate-6 max-w-[62%]"
                  >
                    {item.caption.icon === "youtube" && (
                      <FaYoutube className="text-white shrink-0" size={11} />
                    )}
                    <span className="uppercase tracking-tight">{item.caption.text}</span>
                  </div>
                )}
              </div>
            ))}
            {item.note && (
              <div className="mt-3 bg-obsidian text-white font-outfit font-semibold text-xs md:text-sm px-4 py-2.5 rounded-2xl">
                {item.note}
              </div>
            )}
          </motion.div>
        ))}
        <div className="shrink-0 w-2" />
      </div>
    </section>
  );
};
